import React, { useState, useEffect } from 'react';
import { X, Download, FileText, User, Calendar, Tag, Building } from 'lucide-react';
import Button from '../../../components/ui/Button';
import documentManagementService from '../../../services/documentManagementService';

const DocumentPreviewModal = ({ document, onClose, onDownload }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPreview();
  }, [document]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError('');
      const url = await documentManagementService?.getDocumentUrl(document?.storagePath);
      setPreviewUrl(url);
    } catch (err) {
      console.error('Error loading preview:', err);
      setError('Failed to load document preview');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024)?.toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const canPreview = document?.mimeType?.startsWith('image/') || document?.mimeType === 'application/pdf';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-gray-900 truncate">{document?.documentName}</h2>
            <p className="text-sm text-gray-600 mt-1">{document?.fileName}</p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Button
              onClick={onDownload}
              iconName="Download"
              variant="outline"
              size="sm"
            >
              Download
            </Button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* Preview Area */}
          <div className="flex-1 bg-gray-100 p-6 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading preview...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">{error}</p>
                </div>
              </div>
            ) : !canPreview ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Preview not available for this file type</p>
                  <Button
                    onClick={onDownload}
                    iconName="Download"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Download to View
                  </Button>
                </div>
              </div>
            ) : document?.mimeType === 'application/pdf' ? (
              <iframe
                src={previewUrl}
                className="w-full h-full rounded-lg border border-gray-300"
                title="Document Preview"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <img
                  src={previewUrl}
                  alt={document?.documentName}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                />
              </div>
            )}
          </div>

          {/* Metadata Panel */}
          <div className="w-80 bg-white border-l border-gray-200 p-6 overflow-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Details</h3>

            <div className="space-y-4">
              {/* Employee */}
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <User className="w-4 h-4" />
                  <span>Employee</span>
                </div>
                <p className="text-sm font-medium text-gray-900">{document?.employeeName}</p>
                <p className="text-xs text-gray-600">{document?.employeeNumber}</p>
              </div>

              {/* Department */}
              {document?.department && (
                <div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <Building className="w-4 h-4" />
                    <span>Department</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">{document?.department}</p>
                </div>
              )}

              {/* Category */}
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <FileText className="w-4 h-4" />
                  <span>Category</span>
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {document?.category?.replace('_', ' ')?.toUpperCase()}
                </p>
              </div>

              {/* Document Date */}
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span>Document Date</span>
                </div>
                <p className="text-sm font-medium text-gray-900">{formatDate(document?.documentDate)}</p>
              </div>

              {/* File Info */}
              <div>
                <div className="text-sm text-gray-500 mb-1">File Information</div>
                <div className="text-sm space-y-1">
                  <p className="text-gray-900">
                    <span className="font-medium">Size:</span> {formatFileSize(document?.fileSizeBytes)}
                  </p>
                  <p className="text-gray-900">
                    <span className="font-medium">Type:</span> {document?.mimeType}
                  </p>
                </div>
              </div>

              {/* Description */}
              {document?.description && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Description</div>
                  <p className="text-sm text-gray-900">{document?.description}</p>
                </div>
              )}

              {/* Tags */}
              {document?.tags?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <Tag className="w-4 h-4" />
                    <span>Tags</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {document?.tags?.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Info */}
              <div className="pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-500 mb-1">Upload Information</div>
                <div className="text-xs space-y-1 text-gray-600">
                  <p>
                    <span className="font-medium">Uploaded by:</span> {document?.uploadedBy}
                  </p>
                  <p>
                    <span className="font-medium">Uploaded at:</span>{' '}
                    {formatDate(document?.uploadedAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;